import React, { useEffect, useMemo, useState } from 'react';
import { Printer } from './Icons.jsx';
import { InvoicePdfContent } from './InvoicePdfContent.jsx';
import { QuotePdfContent } from './QuotePdfContent.jsx';

export function PrintView({ documentData, customer, type = 'invoice', settings, onClose, onDownloadPdf, pdfUrl = null, htmlUrl = null }) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfRetryTick, setPdfRetryTick] = useState(0);

  const primaryColor = settings.primaryColor || '#2563eb';
  const safePdfPart = (name) => {
    if (!name || typeof name !== 'string') return 'Kunde';
    const n = String(name)
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue');
    return n.replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_').trim().slice(0, 60) || 'Kunde';
  };

  const pdfTitle = useMemo(() => {
    const id = documentData?.id != null ? String(documentData.id) : '';
    const customerName = safePdfPart(customer?.name);
    const prefix = type === 'invoice' ? 'Rechnung' : 'Angebot';
    return id ? `${prefix}_${customerName}_${id}` : `${prefix}_${customerName}`;
  }, [documentData?.id, customer?.name, type]);

  useEffect(() => {
    const prev = document.title;
    document.title = `${pdfTitle}.pdf`;
    return () => {
      document.title = prev;
    };
  }, [pdfTitle]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (!pdfUrl) {
      setPdfBlobUrl(null);
      setPdfLoading(false);
      setPdfError('');
      return () => { };
    }

    const run = async () => {
      setPdfLoading(true);
      setPdfError('');
      const attempts = 3;
      for (let i = 1; i <= attempts; i += 1) {
        try {
          const res = await fetch(pdfUrl, { credentials: 'include' });
          if (!res.ok) {
            let backendMsg = '';
            try {
              const text = await res.text();
              if (text) {
                try {
                  const parsed = JSON.parse(text);
                  backendMsg = parsed?.error ? String(parsed.error) : text.slice(0, 240);
                } catch (_) {
                  backendMsg = text.slice(0, 240);
                }
              }
            } catch (_) { }
            throw new Error(backendMsg ? `HTTP ${res.status}: ${backendMsg}` : `HTTP ${res.status}`);
          }
          const blob = await res.blob();
          if (!blob || blob.size < 1500) throw new Error('Leere PDF-Antwort');
          objectUrl = URL.createObjectURL(blob);
          if (!cancelled) {
            setPdfBlobUrl(objectUrl);
            setPdfLoading(false);
            setPdfError('');
          }
          return;
        } catch (err) {
          if (i === attempts) {
            if (!cancelled) {
              setPdfLoading(false);
              const msg = (err && err.message) ? String(err.message) : '';
              setPdfError(msg ? `PDF-Vorschau konnte nicht geladen werden: ${msg}` : 'PDF-Vorschau konnte nicht geladen werden. Bitte erneut versuchen.');
            }
          } else {
            await new Promise((r) => setTimeout(r, 350 * i));
          }
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfUrl, pdfRetryTick]);

  const handlePdfDownload = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // Lokaler Fallback ohne API: Browser-Print/Save-as-PDF.
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-center overflow-y-auto print:bg-white print:absolute print:inset-0">
      <div className="bg-white w-full max-w-full sm:max-w-4xl min-h-screen md:min-h-0 md:my-10 md:rounded-lg shadow-2xl flex flex-col print:shadow-none print:m-0 print:w-full print:max-w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 sm:p-4 border-b border-slate-100 print:hidden gap-3">
          <h2 className="font-semibold text-slate-700 text-base sm:text-lg">
            {type === 'invoice' ? 'Rechnungsvorschau' : 'Angebotsvorschau'}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg min-h-[44px] touch-manipulation">
              Schließen
            </button>
            <button
              type="button"
              onClick={handlePdfDownload}
              className="flex items-center justify-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 min-h-[44px] touch-manipulation"
              style={{ backgroundColor: primaryColor }}
            >
              <Printer size={18} />
              <span>{pdfUrl ? 'PDF öffnen / speichern' : 'Als PDF speichern'}</span>
            </button>
          </div>
        </div>
        {pdfUrl ? (
          <div className="flex-1 bg-slate-100 p-2 sm:p-3 print:hidden">
            {pdfLoading && (
              <div className="w-full h-[80vh] md:h-[85vh] bg-white rounded-lg border border-slate-200 flex items-center justify-center text-slate-500">
                PDF wird geladen...
              </div>
            )}
            {!pdfLoading && pdfError && (
              <div className="w-full h-[80vh] md:h-[85vh] bg-white rounded-lg border border-slate-200 flex flex-col items-center justify-center gap-3">
                <p className="text-slate-600 text-sm">{pdfError}</p>
                <button
                  type="button"
                  onClick={() => {
                    setPdfBlobUrl(null);
                    setPdfError('');
                    setPdfLoading(false);
                    setPdfRetryTick((v) => v + 1);
                  }}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Neu laden
                </button>
              </div>
            )}
            {!pdfLoading && !pdfError && pdfBlobUrl && (
              <iframe
                title="PDF Vorschau"
                src={pdfBlobUrl}
                className="w-full h-[80vh] md:h-[85vh] bg-white rounded-lg border border-slate-200"
              />
            )}
          </div>
        ) : htmlUrl ? (
          <div className="flex-1 bg-slate-100 p-2 sm:p-3 print:hidden">
            <iframe
              title="HTML Vorschau"
              src={htmlUrl}
              className="w-full h-[80vh] md:h-[85vh] bg-white rounded-lg border border-slate-200"
            />
          </div>
        ) : (
          <div className="overflow-auto bg-slate-100 p-3 sm:p-6 print:bg-white print:p-0 print:overflow-visible">
            <div className="mx-auto w-[210mm] max-w-full bg-white shadow-[0_10px_30px_rgba(15,23,42,0.14)] print:shadow-none">
              {type === 'quote' ? (
                <QuotePdfContent documentData={documentData} customer={customer} settings={settings} />
              ) : (
                <InvoicePdfContent documentData={documentData} customer={customer} type={type} settings={settings} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
